import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import useSidebarResizing from '../hooks/useSidebarResizing';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { toggleLikesSidebar, setLikesSidebarWidth } from '../redux/slices/likesSlice';
import { fetchLikes, unlikeTrack } from '../redux/thunk/likesThunk';
import { fetchLikedMixes, unlikeMix } from '../redux/thunk/likedMixesThunk';
import { isSupportedMixUrl, useMixPlayback, mixTrackingKey } from './DJPal/MixPlayer';
import { setActiveSearch, setTrackQuery, setArtistQuery, setMixQuery } from '../redux/slices/djpalSlice';
import { searchTrack, searchArtist, searchMixes } from '../redux/thunk/djpalThunk';
import { setActiveTab } from '../redux/slices/appSlice';
import { resetAudioList, addTrack as addTrackToQueue, setAutoPlay, setLikesPlayback, setLikesPlaybackOffset } from '../redux/slices/musicPlayerSlice';
import { clearListContext } from '../redux/slices/listContextSlice';
import { resolveCover } from '../utils/cover';
import { useScrollMemory } from '../hooks/useScrollMemory';
import { useIsMobileWeb } from '../hooks/useIsMobileWeb';
import MobileListSearch, { matchesQuery } from './common/MobileListSearch';
import { isNative } from '../utils/webOnly';
import { resolveMixCover, useNativeMixCoverMode } from '../utils/nativeMixCover';
import { resolveTrackSrc, resolveMixSrc } from '../utils/resolvePlaybackSrc';
import { entityId } from '../utils/entityId';
import { useToast } from '../contexts/ToastContext';
import { track as trackEvent } from '../utils/analytics';
import DJPalAudioPreview from './DJPal/DJPalAudioPreview';
import { delayedClick } from './DJPal/MixPlayer';
import TrackContextMenu from './TrackContextMenu';
import MixContextMenu from './MixContextMenu';
import NotifyBellButton from './NotifyBellButton';
import MultiArtistText from './MultiArtistText';
import EntityHoverCard from './EntityHoverCard';
import CoverArt from './CoverArt';
import FeedWaveform from './FeedWaveform';
import FakeTrackWaveform from './FakeTrackWaveform';
import { useFeedWaveformMode } from '../utils/feedWaveform';
import { useDesignV2 } from '../utils/designV2';
import { setPendingStart } from '../redux/slices/whatsNextSlice';
import { ReactComponent as PlayIcon } from '../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../assets/icons/pause.svg';
import SidebarSearchField from './common/SidebarSearchField';
import ViewToggle, { useViewMode } from './common/ViewToggle';
import './LikedTracksSidebar.scss';
import './DJPal/DJPal.scss';

// Older mix likes saved with the date baked into the title, e.g.
// "CLR Podcast 471 (2026-03-23)". Strip the trailing date for display and
// for any operation that uses the title (search, playback id).
const stripMixDateSuffix = (title) =>
    typeof title === 'string'
        ? title.replace(/\s*\(\d{4}-\d{2}-\d{2}\)\s*$/, '')
        : title;

// Text a like row is searched by (artist + title for tracks, title + DJ for mixes).
const likeSearchText = (i) => (i._type === 'track'
    ? `${i.artist || ''} ${i.title || ''}`
    : `${i.title || ''} ${i.dj_name || ''}`);

const LikedTracksSidebar = ({ asPage = false }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    useNativeMixCoverMode(); // re-render when the native cover flag flips
    const [filter, setFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const isMobileWeb = useIsMobileWeb();
    const { likedTracks, loading: tracksLoading, sidebarOpen, sidebarWidth } = useSelector(state => state.likes);
    // Play All button state: `likesPlayback` marks the global queue as ours,
    // `isPlaying` is the player's desktop-truth play state (onAudioPlay/Pause).
    const likesPlayback = useSelector(state => state.musicPlayer.likesPlayback);
    const playerIsPlaying = useSelector(state => state.musicPlayer.isPlaying);
    const isVisible = asPage || sidebarOpen;
    const v2 = useDesignV2();
    const waveformMode = useFeedWaveformMode();
    const [viewMode, setViewMode] = useViewMode('likes');
    // v2 renders the feed cards in pages of 20 — each card carries a waveform,
    // and 50+ at once held the page for seconds. More arrive as you scroll.
    const [v2Visible, setV2Visible] = useState(20);
    // v2: the first paint is the page frame + ghost rows; the 20 feed cards
    // (each with a waveform) mount on the next frame. Rendering them in the
    // same task as the open froze the renderer for ~1.8s, so the page seemed
    // to wait for the server when it was waiting for itself.
    const [v2Settled, setV2Settled] = useState(false);
    useEffect(() => {
        if (!isVisible) { setV2Settled(false); return undefined; }
        let t; const raf = requestAnimationFrame(() => { t = setTimeout(() => setV2Settled(true), 0); });
        return () => { cancelAnimationFrame(raf); clearTimeout(t); };
    }, [isVisible]);
    const v2MoreRef = useRef(null);
    useEffect(() => {
        if (!v2 || !v2MoreRef.current) return undefined;
        const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) setV2Visible((n) => n + 20); }, { root: scrollRef.current, rootMargin: '600px' });
        io.observe(v2MoreRef.current);
        return () => io.disconnect();
    }, [v2, v2Visible]);
    const [v2CopiedId, setV2CopiedId] = useState(null);
    // DESIGN V2 — the For You action set on a liked track.
    const v2Copy = (e, item, id) => { e.stopPropagation(); navigator.clipboard.writeText(item.artist ? `${item.artist} - ${item.title}` : (item.title || '')).catch(() => {}); setV2CopiedId(id); setTimeout(() => setV2CopiedId(null), 1500); };
    const v2Queue = async (e, item) => { e.stopPropagation(); const resolved = await resolveItem(item); if (resolved?.musicSrc) queueItem(item, resolved); };
    const v2StartSet = (e, item) => { e.stopPropagation(); const title = (item.title || '').trim(); if (!title) return; const artist = (item.artist || '').trim(); dispatch(setPendingStart({ query: artist ? `${artist} - ${title}` : title, meta: { title, artist, artworkUrl: item.artwork || null } })); dispatch(setActiveTab('whats-next')); };
    const [isResizing, setIsResizing] = useState(false);
    useSidebarResizing(isResizing);
    // True from the Play All click until the first track is queued — the first
    // resolve can take seconds (lazy YT search + yt-dlp download), and with no
    // feedback the button read as dead (user clicked it repeatedly).
    const [startingAll, setStartingAll] = useState(false);
    // Post-start grace: the native playerState stream briefly dips
    // isPlaying=false while a cold-started queue buffers, flickering the
    // button Pause → Resume → Pause right after the spinner clears (first
    // click only, mobile). Hold Pause through the dips; a real tap ends the
    // grace immediately. Same fix as PlayAllButton.
    const [playAllGrace, setPlayAllGrace] = useState(false);
    const playAllGraceTimerRef = React.useRef(null);
    React.useEffect(() => () => clearTimeout(playAllGraceTimerRef.current), []);

    useEffect(() => {
        if (!isResizing) return;
        const handleMouseMove = (e) => {
            const rail = 52;
            const newWidth = e.clientX - rail;
            if (newWidth >= 240 && newWidth <= 520) {
                dispatch(setLikesSidebarWidth(newWidth));
            }
        };
        const handleMouseUp = () => setIsResizing(false);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, dispatch]);
    const { likedMixes, loading: mixesLoading } = useSelector(state => state.likedMixes);
    const { isAuthenticated } = useSelector(state => state.auth);
    const { playMix, loadingMixId, playingMixId } = useMixPlayback();
    // Leave-and-return keeps the scroll position (module-level store — this
    // component unmounts on every tab switch). Deps: both lists feed
    // mergedItems, which is declared below this hooks block.
    const scrollRef = useScrollMemory('likes', [likedTracks.length, likedMixes.length]);
    useEffect(() => {
        if (isVisible && isAuthenticated) {
            dispatch(fetchLikes());
            dispatch(fetchLikedMixes());
        }
    }, [isVisible, isAuthenticated, dispatch]);

    // Tracks + mixes under the current Tracks/Mixes chip, newest first — the
    // base both the list (after the search query) and the phone search
    // overlay (which filters live itself) work from.
    const chipItems = useMemo(() => {
        const tracks = likedTracks.map(t => ({ ...t, _type: 'track' }));
        const mixes = likedMixes.map(m => ({ ...m, _type: 'mix' }));
        let all = [...tracks, ...mixes].sort((a, b) =>
            new Date(b.created_at) - new Date(a.created_at)
        );
        if (filter === 'tracks') all = all.filter(i => i._type === 'track');
        if (filter === 'mixes') all = all.filter(i => i._type === 'mix');
        return all;
    }, [likedTracks, likedMixes, filter]);
    const mergedItems = useMemo(() => {
        if (!searchQuery.trim()) return chipItems;
        // Every word must match somewhere, any order — "prospa baby" has
        // to find "Prospa, Murda Beatz - Baby", which a whole-query
        // substring test never does (matchesQuery).
        return chipItems.filter(i => matchesQuery(likeSearchText(i), searchQuery));
    }, [chipItems, searchQuery]);

    const toggleFilter = (type) => {
        setFilter(prev => prev === type ? 'all' : type);
    };

    const handleUnlikeTrack = useCallback((e, track) => {
        e.stopPropagation();
        dispatch(unlikeTrack({ artist: track.artist, title: track.title }));
    }, [dispatch]);

    const handleUnlikeMix = useCallback((e, mix) => {
        e.stopPropagation();
        dispatch(unlikeMix({ title: mix.title, djName: mix.dj_name || mix.artist }));
    }, [dispatch]);

    // All three handlers: sub-tab + query BEFORE setActiveTab so the nav marker
    // is typed and the result replaces it (one Back = back here). setActiveTab
    // sets the category itself — a separate setActiveCategory pushes a stray
    // landing-page entry into the back stack.
    const handleSearchTrack = useCallback((e, track) => {
        e.stopPropagation();
        const query = track.artist ? `${track.artist} - ${track.title}` : track.title;
        dispatch(setActiveSearch('track'));
        dispatch(setTrackQuery(query));
        dispatch(setActiveTab('djpal'));
        setTimeout(() => {
            // Rows come from the likes slice, which is boundary-normalized in
            // likesThunk (fromLikeRow): the like-row id is parked on `likeId`
            // and `.id` IS the catalog public id, so entityId() is safe here.
            dispatch(searchTrack({ trackName: query, trackId: entityId(track), variant: 'A', artworkUrl: track.artwork || track.artworkUrl || track.entityArtwork || null }));
        }, 150);
    }, [dispatch]);

    const handleSearchMix = useCallback((e, mix) => {
        e.stopPropagation();
        dispatch(setActiveSearch('mix'));
        dispatch(setMixQuery(mix.title || ''));
        dispatch(setActiveTab('djpal'));
        // Id-first, always: liked-mix rows are boundary-normalized so `.id`
        // IS the catalog public id (from track_public_id or the by-link
        // enrichment). The link/title branches are LEGACY-ONLY — pre-id like
        // rows the user opted not to backfill — and never run for new likes.
        const mixPublicId = entityId(mix);
        const mixFilter = mixPublicId ? { public_id: mixPublicId }
            : mix.link ? { link: mix.link }
            : { title: mix.title };
        setTimeout(() => {
            dispatch(searchMixes({ filters: mixFilter }));
        }, 150);
    }, [dispatch]);

    const handleArtistClick = useCallback((e, artistName, artistId = null, artworkUrl = null) => {
        e.stopPropagation();
        dispatch(setActiveSearch('artist'));
        dispatch(setArtistQuery(artistName));
        dispatch(setActiveTab('djpal'));
        setTimeout(() => {
            dispatch(searchArtist({ artistName, artistId, artworkUrl: artworkUrl || null }));
        }, 150);
    }, [dispatch]);

    // The For You artist preview (photo + Follow) on the card list's artist
    // names, as in For You. Not on the compact rows: dense lists get no hover cards.
    const artistHoverWrap = useCallback((seg, node) => (
        <EntityHoverCard type="artist" entity={seg} onNavigate={() => handleArtistClick({ stopPropagation: () => {} }, seg.name, seg.id, seg.artworkUrl)}>
            {node}
        </EntityHoverCard>
    ), [handleArtistClick]);

    const playAllRef = React.useRef(false);

    // Routes through the shared resolveTrackSrc / resolveMixSrc so sample-only
    // tracks get a lazy YT search and dead YT ids get the replacement path.
    const resolveItem = async (item) => {
        if (item._type === 'mix') {
            return resolveMixSrc({ link: item.link, playbackBlocked: item.playbackBlocked });
        }
        // A liked track backed by a local file on this machine plays from that
        // file directly — never resolve a web source for it.
        if (item.local_file_path) {
            return { musicSrc: `http://localhost:3069/audio?path=${encodeURIComponent(item.local_file_path)}` };
        }
        // Normalize snake_case fields the likes API returns into the shape
        // resolveTrackSrc expects (camelCase: youtubeId, soundcloudUrl,
        // previewUrl, id).
        return resolveTrackSrc({
            ...item,
            youtubeId: item.youtubeId || item.youtube_id,
            soundcloudUrl: item.soundcloudUrl || item.soundcloud_url,
            previewUrl: item.previewUrl || item.preview_url,
            id: entityId(item),
        });
    };

    const queueItem = (item, resolved) => {
        dispatch(addTrackToQueue({
            name: item.title,
            singer: item._type === 'track' ? (item.artist || '') : (item.dj_name || ''),
            cover: resolveCover(item.artwork),
            musicSrc: resolved.musicSrc,
            ...(resolved.isHls ? { isHls: true, hlsSrc: resolved.hlsSrc } : {}),
            ...(resolved.originalUrl ? { originalUrl: resolved.originalUrl } : {}),
        }));
    };

    const handlePlayAll = useCallback(async () => {
        if (mergedItems.length === 0 || playAllRef.current) return;
        playAllRef.current = true;
        setStartingAll(true);
        trackEvent('play_all_clicked', { from: 'likes', action: 'start' });

        // Get playable items
        const playable = mergedItems.filter(item =>
            item._type === 'track'
                ? (item.local_file_path || item.preview_url || item.soundcloud_url || item.youtube_id)
                : (item._type === 'mix' && (item.link || item.preview_url))
        );

        if (playable.length === 0) {
            toast.warning('No playable items');
            playAllRef.current = false;
            setStartingAll(false);
            return;
        }

        // Resolve first track upfront so playback starts fast.
        const first = playable[0];
        const firstResolved = await resolveItem(first);
        if (!firstResolved) {
            toast.warning('Could not load any items');
            playAllRef.current = false;
            setStartingAll(false);
            return;
        }

        // Likes playback owns the queue from here (its own refill batches) —
        // a listContext left over from an earlier feed/Mixpedia play must not
        // survive, or the native queue effect keeps playing the old surface
        // and desktop resumes it when the likes batch runs out.
        dispatch(clearListContext());
        dispatch(resetAudioList());
        dispatch(setLikesPlayback(true));
        dispatch(setLikesPlaybackOffset(20));
        queueItem(first, firstResolved);
        dispatch(setAutoPlay(true));
        // Hold the spinner until the player actually STARTS. Clearing here is
        // a beat too early — addTrack → jinke init → onAudioPlay takes up to
        // ~1s, and the button flashed back to the idle triangle in that gap
        // before flipping to pause. Registered after the dispatches, so the
        // reset's own pause broadcast (playing:false) can't clear it; only
        // the new track's playing:true does. Timeout = never spin forever if
        // the audio element errors instead of playing.
        const clearStarting = () => {
            setStartingAll(false);
            setPlayAllGrace(true);
            clearTimeout(playAllGraceTimerRef.current);
            playAllGraceTimerRef.current = setTimeout(() => setPlayAllGrace(false), 1600);
            window.removeEventListener('player-track-change', onPlaying);
        };
        const onPlaying = (e) => { if (e.detail?.playing) clearStarting(); };
        window.addEventListener('player-track-change', onPlaying);
        setTimeout(clearStarting, 12000);

        // Resolve up to 19 more sequentially in the background (total 20).
        (async () => {
            for (const item of playable.slice(1, 20)) {
                const resolved = await resolveItem(item);
                if (resolved) queueItem(item, resolved);
            }
            playAllRef.current = false;
        })();
    }, [dispatch, mergedItems, toast]);

    // Pause/resume the likes queue in place. Desktop/web: the audio element
    // is the real player and jinke's onAudioPlay/onAudioPause sync Redux +
    // broadcast `player-track-change`. Native: the element is a muted mirror
    // (see native-queue rules) — route through the hijacked jinke play/pause
    // button, the same door NowPlayingPage and the playlist sidebar use; the
    // plugin's playerState stream then updates Redux isPlaying + broadcasts.
    const handleToggleLikesPlayback = useCallback(() => {
        // Deliberate tap → the shown state must be the real one from here on.
        clearTimeout(playAllGraceTimerRef.current);
        setPlayAllGrace(false);
        trackEvent('play_all_clicked', { from: 'likes', action: playerIsPlaying ? 'pause' : 'resume' });
        if (isNative) {
            const btn = document.querySelector('.react-jinke-music-player-main .player-content .play-btn');
            if (btn) btn.click();
            return;
        }
        const audio = window.musicPlayerInstance;
        if (!audio) return;
        if (audio.paused) audio.play().catch(() => {});
        else audio.pause();
    }, [playerIsPlaying]);

    // Toggle mode: the global queue is ours (set by handlePlayAll, cleared by
    // MusicPlayer when the likes run out). While true, this button controls
    // pause/resume instead of rebuilding the queue from the top.
    const likesQueueActive = likesPlayback;

    if (!isVisible) return null;

    const isLoading = (tracksLoading && mixesLoading && mergedItems.length === 0) || (v2 && !v2Settled);

    const handleClose = () => {
        if (asPage) {
            // The in-page arrow is "up to Library", not browser Back: this
            // page is a Library sub-page, and history.back() sent the user
            // to whatever tab they visited before Library (e.g. Digger).
            // The OS/browser back gesture still walks history as before.
            dispatch(setActiveTab('library'));
        } else {
            dispatch(toggleLikesSidebar());
        }
    };

    return (
        <div className={`likes-sidebar${asPage ? ' as-page' : ''}`} style={asPage ? undefined : { width: `${sidebarWidth}px` }}>
            <div className="sidebar-header">
                <div className="header-title">
                    <h4>Likes</h4>
                    <button
                        className="sidebar-collapse-btn"
                        onClick={handleClose}
                        title={asPage ? 'Back' : 'Hide Likes'}
                    >
                        {asPage ? '←' : '‹'}
                    </button>
                </div>
                <div className="filter-row">
                    <button
                        className={`filter-chip ${filter === 'tracks' ? 'active' : ''}`}
                        onClick={() => toggleFilter('tracks')}
                        title="Tracks only"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="5.5" cy="17.5" r="2.5"/>
                            <circle cx="17.5" cy="15.5" r="2.5"/>
                            <path d="M8 17.5V5l12-2v12.5"/>
                        </svg>
                        Tracks
                    </button>
                    <button
                        className={`filter-chip ${filter === 'mixes' ? 'active' : ''}`}
                        onClick={() => toggleFilter('mixes')}
                        title="Mixes only"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
                            <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5z"/>
                            <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5z"/>
                        </svg>
                        Mixes
                    </button>
                    {mergedItems.length > 0 && (
                        <button
                            className={`play-all-btn${startingAll ? ' loading' : likesQueueActive && (playerIsPlaying || playAllGrace) ? ' playing' : ''}`}
                            onClick={startingAll ? undefined : (likesQueueActive ? handleToggleLikesPlayback : handlePlayAll)}
                            title={startingAll ? 'Loading…' : likesQueueActive ? ((playerIsPlaying || playAllGrace) ? 'Pause' : 'Resume') : 'Play all'}
                        >
                            {startingAll ? (
                                <span className="play-all-spinner" style={{ width: 14, height: 14 }} />
                            ) : likesQueueActive && (playerIsPlaying || playAllGrace) ? (
                                <PauseIcon width="14" height="14" />
                            ) : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M8 5v14l11-7z"/>
                                </svg>
                            )}
                            {v2 && <span className="play-all-label">{likesQueueActive && (playerIsPlaying || playAllGrace) ? 'Pause' : 'Play all'}</span>}
                        </button>
                    )}
                </div>
                <div className="search-input-wrapper">
                    {isMobileWeb ? (
                        <MobileListSearch
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder="Search likes"
                            ariaLabel="Search likes"
                            overlay={{
                                fetch: (q) => chipItems
                                    .filter(i => matchesQuery(likeSearchText(i), q))
                                    .map((i, idx) => (i._type === 'track'
                                        ? {
                                            key: `t-${idx}`,
                                            title: i.title,
                                            subtitle: i.artist || (i.artists || []).map(a => a.name).join(', ') || null,
                                            artworkUrl: i.artwork || (i.local_file_path ? `http://localhost:3069/album-art?path=${encodeURIComponent(i.local_file_path)}` : null),
                                            item: i,
                                        }
                                        : {
                                            key: `m-${idx}`,
                                            title: stripMixDateSuffix(i.title),
                                            subtitle: i.dj_name || null,
                                            artworkUrl: resolveMixCover(i) || null,
                                            item: i,
                                        })),
                                onSelect: (row) => {
                                    const i = row.item;
                                    if (i._type === 'mix') handleSearchMix({ stopPropagation: () => {} }, { ...i, title: stripMixDateSuffix(i.title) });
                                    else handleSearchTrack({ stopPropagation: () => {} }, i);
                                },
                            }}
                        />
                    ) : (
                        <SidebarSearchField
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder="Search likes"
                            ariaLabel="Search likes"
                        />
                    )}
                </div>
                {v2 && !asPage && <ViewToggle mode={viewMode} onChange={setViewMode} />}
            </div>

            <div className="sidebar-content" ref={scrollRef}>
                {isLoading ? (
                    <div className="text-center p-4"><Spinner animation="border" size="sm" /></div>
                ) : mergedItems.length === 0 ? (
                    <div className="empty-state">
                        <svg className="empty-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                        </svg>
                        {searchQuery.trim() ? (
                            <>
                                <p>Nothing matches &ldquo;{searchQuery.trim()}&rdquo;</p>
                                <p className="empty-hint">Try another name, or clear the search</p>
                            </>
                        ) : (
                            <>
                                <p>No likes yet</p>
                                <p className="empty-hint">Like tracks and mixes from Mixpedia to save them here</p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className={`likes-list${v2 && !asPage && viewMode === 'list' ? ' is-list' : ''}`}>
                        {(v2 ? mergedItems.slice(0, v2Visible) : mergedItems).map((item, idx) => {
                            if (item._type === 'track') {
                                const previewId = `liked-${idx}`;
                                // A liked track that exists as a local file on this
                                // machine plays from that file — never from a web
                                // source. Web (preview/YouTube/SoundCloud) is only
                                // for catalog-only likes, or if the file has since
                                // moved/been deleted (local_file_path cleared on load).
                                const localSrc = item.local_file_path
                                    ? `http://localhost:3069/audio?path=${encodeURIComponent(item.local_file_path)}`
                                    : null;
                                const src = localSrc || item.preview_url || item.soundcloud_url || null;
                                const youtubeId = !localSrc && item.youtube_id && item.youtube_id !== '-1' ? item.youtube_id : null;
                                const scUrl = !localSrc ? (item.soundcloud_url || null) : null;
                                // No stored artwork (a like of a local file with
                                // no catalog match): on the machine that has the
                                // file, fall back to its embedded album art via
                                // the local audio server. local_file_path is only
                                // ever set on desktop, so web never builds this.
                                const art = item.artwork || (item.local_file_path
                                    ? `http://localhost:3069/album-art?path=${encodeURIComponent(item.local_file_path)}`
                                    : null);
                                if (v2) {
                                    // DESIGN V2 — the For You card: art, play disc, waveform, actions.
                                    return (
                                        <div className="feed-card v2-like-card" key={`t-${item.artist}-${item.title}-${idx}`}>
                                            <div className="feed-card-body">
                                                <div className="feed-card-artwork feed-clickable" onClick={(e) => { e.stopPropagation(); handleSearchTrack(e, item); }}>
                                                    {art ? <CoverArt src={art} size={120} alt={item.title} /> : <div className="feed-artwork-placeholder" aria-hidden="true" />}
                                                </div>
                                                <div className="feed-card-content">
                                                    <div className="feed-card-track-info">
                                                        <DJPalAudioPreview src={src} youtubeId={youtubeId} soundcloudUrl={scUrl} previewId={previewId} trackId={localSrc ? null : entityId(item)} durationSec={item.durationSec ?? item.duration} pageUrl={item.pageUrl} trackName={item.title} trackArtist={item.artist} trackArtists={item.artists} trackCover={art} playContext={{ items: mergedItems, index: idx, type: 'mixed' }} playbackBlocked={item.playbackBlocked}>
                                                            <button className="feed-play-btn" onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('djpal-row-play', { detail: previewId })); }}>
                                                                <svg className="feed-icon-play" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3" /></svg>
                                                                <svg className="feed-icon-pause" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="3" width="5" height="18" rx="1" /><rect x="14" y="3" width="5" height="18" rx="1" /></svg>
                                                            </button>
                                                        </DJPalAudioPreview>
                                                        <div className="feed-card-text">
                                                            <span className="feed-card-title feed-clickable" onClick={(e) => { e.stopPropagation(); handleSearchTrack(e, item); }}>{item.title}</span>
                                                            {(item.artist || (item.artists && item.artists.length > 0)) && (
                                                                <MultiArtistText artists={item.artists} fallbackText={item.artist} onSelect={(a) => handleArtistClick({ stopPropagation: () => {} }, a.name, a.id, a.artworkUrl)} className="feed-card-artist" segmentClassName="feed-clickable" segmentWrapper={artistHoverWrap} />
                                                            )}
                                                        </div>
                                                    </div>
                                                    {!localSrc && (
                                                        // Same as For You: a placeholder until the track is played
                                                        // (FEED_WAVEFORM_MODE=FAKE), so opening Likes downloads nothing.
                                                        <div className={`feed-card-waveform-wrapper${waveformMode === 'FAKE' ? ' feed-card-waveform-mix' : ''}`}
                                                            onClick={waveformMode === 'FAKE' ? (e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('djpal-row-play', { detail: previewId })); } : undefined}>
                                                            {waveformMode === 'FAKE' && <FakeTrackWaveform seed={idx + 1} />}
                                                            <FeedWaveform src={src} youtubeId={youtubeId} soundcloudUrl={scUrl} trackId={entityId(item)} index={idx} previewId={previewId} playbackBlocked={item.playbackBlocked} mode={waveformMode} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="feed-card-actions">
                                                <button className="track-like-btn liked" title="Unlike" onClick={(e) => handleUnlikeTrack(e, item)}>
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
                                                </button>
                                                <button type="button" className="feed-action-btn" title="Copy track name" onClick={(e) => v2Copy(e, item, previewId)}>
                                                    {v2CopiedId === previewId
                                                        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>}
                                                </button>
                                                <button type="button" className="feed-action-btn feed-queue-btn" title="Add to Next Up" onClick={(e) => v2Queue(e, item)}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
                                                </button>
                                                {!localSrc && (
                                                    <button type="button" className="feed-action-btn" title="Start a set with this track" onClick={(e) => v2StartSet(e, item)}>
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="2.5" /><path d="M7.5 12 H20" /><path d="M16 8l4 4-4 4" /></svg>
                                                    </button>
                                                )}
                                                <NotifyBellButton entityType="track" entity={item} from="sidebar" />
                                                {/* List view folds the strip into the menu (Unlike lives there too); the bell stays outside. */}
                                                <TrackContextMenu track={item} desktopLike={v2 && !asPage && viewMode === 'list'} />
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <div
                                        className="djpal-track-row clickable"
                                        key={`t-${item.artist}-${item.title}-${idx}`}
                                        {...delayedClick(() => handleSearchTrack({ stopPropagation: () => {} }, item))}
                                    >
                                        <DJPalAudioPreview
                                            src={src}
                                            youtubeId={youtubeId}
                                            soundcloudUrl={scUrl}
                                            previewId={previewId}
                                            trackId={localSrc ? null : entityId(item)}
                                            durationSec={item.durationSec ?? item.duration}
                                            pageUrl={item.pageUrl}
                                            trackName={item.title}
                                            trackArtist={item.artist}
                                            trackArtists={item.artists}
                                            trackCover={art}
                                            playContext={{ items: mergedItems, index: idx, type: 'mixed' }}
                                            playbackBlocked={item.playbackBlocked}
                                        >
                                            <div className="track-artwork">
                                                {art ? <CoverArt src={art} size={48} /> : <div className="artwork-placeholder">&#9835;</div>}
                                            </div>
                                        </DJPalAudioPreview>
                                        <div className="track-info track-info-stacked">
                                            <span className="track-title">{item.title}</span>
                                            {(item.artist || (item.artists && item.artists.length > 0)) && (
                                                <MultiArtistText
                                                    artists={item.artists}
                                                    fallbackText={item.artist}
                                                    onSelect={(a) => handleArtistClick({ stopPropagation: () => {} }, a.name, a.id, a.artworkUrl)}
                                                    className="track-artist"
                                                    segmentClassName="clickable-artist"
                                                />
                                            )}
                                        </div>
                                        <NotifyBellButton entityType="track" entity={item} from="sidebar" />
                                        <button
                                            className="track-like-btn liked"
                                            title="Unlike"
                                            onClick={(e) => handleUnlikeTrack(e, item)}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                            </svg>
                                        </button>
                                        <TrackContextMenu track={item} />
                                    </div>
                                );
                            } else {
                                const art = resolveMixCover(item);
                                // Native (iOS + Android): mixes are not playable — hide the play overlay (see canPlayMix).
                                const canPlay = !isNative && !!(item.link);
                                const cleanTitle = stripMixDateSuffix(item.title);
                                // Real catalog id (rows are boundary-normalized in
                                // fetchLikedMixes) or nothing — never a fabricated
                                // "liked-mix-<title>" id, which used to leak into the
                                // queue's mixId and break player-bar navigation.
                                const mixForPlay = {
                                    id: entityId(item) || null,
                                    title: cleanTitle,
                                    link: item.link,
                                    artwork: art,
                                    djName: item.dj_name,
                                };
                                const trackKey = mixTrackingKey(mixForPlay);
                                const mixIsLoading = loadingMixId === trackKey;
                                const mixIsPlaying = playingMixId === trackKey;
                                const handlePlayMix = () => {
                                    if (!canPlay) return;
                                    playMix(mixForPlay, { items: mergedItems, index: idx, type: 'mixed' });
                                };
                                return (
                                    <div
                                        className="djpal-track-row clickable"
                                        key={`m-${item.title}-${idx}`}
                                        {...delayedClick(() => handleSearchMix({ stopPropagation: () => {} }, { ...item, title: cleanTitle }))}
                                    >
                                        <div
                                            className={`track-artwork${canPlay ? ' clickable' : ''}`}
                                            onClick={canPlay ? (e) => { e.stopPropagation(); handlePlayMix(); } : undefined}
                                        >
                                            {canPlay && (
                                                <div className={`mix-play-overlay${mixIsLoading ? ' loading' : ''}${mixIsPlaying ? ' playing' : ''}`}>
                                                    {mixIsLoading ? <span className="mix-play-spinner" /> : <span className={`play-icon${mixIsPlaying ? ' paused' : ''}`}>{mixIsPlaying ? <PauseIcon /> : <PlayIcon />}</span>}
                                                </div>
                                            )}
                                            {art ? <CoverArt src={art} size={48} /> : <div className="artwork-placeholder">{v2 ? null : <>&#9835;</>}</div>}
                                        </div>
                                        <div className="track-info track-info-stacked">
                                            <span className="track-title">{
                                                item.dj_name && cleanTitle
                                                    ? cleanTitle.replace(new RegExp(`^${item.dj_name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[-–—@]\\s*`, 'i'), '')
                                                    : cleanTitle
                                            }</span>
                                            {(item.dj_name || (item.artists && item.artists.length > 0)) && (
                                                <MultiArtistText
                                                    artists={item.artists}
                                                    fallbackText={item.dj_name}
                                                    onSelect={(a) => handleArtistClick({ stopPropagation: () => {} }, a.name, a.id, a.artworkUrl)}
                                                    className="track-artist"
                                                    segmentClassName="clickable-artist"
                                                />
                                            )}
                                        </div>
                                        <button
                                            className="track-like-btn liked"
                                            title="Unlike"
                                            onClick={(e) => handleUnlikeMix(e, item)}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                            </svg>
                                        </button>
                                        <MixContextMenu mix={{ ...item, title: cleanTitle }} />
                                    </div>
                                );
                            }
                        })}
                        {v2 && v2Visible < mergedItems.length && <div ref={v2MoreRef} className="v2-more-sentinel" aria-hidden="true" />}
                    </div>
                )}
            </div>
            <div
                className="sidebar-resizer"
                onMouseDown={() => setIsResizing(true)}
            />
        </div>
    );
};

export default LikedTracksSidebar;
